import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, ArrowRight, CalendarPlus, FunnelSimple } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { formatEventSchedule } from "@/lib/milestones";

const BLANK = { event_type: "Wedding", couple_names: "", event_date: "", event_time: "", venue: "Gledswood Homestead + Winery" };

export default function EventsList() {
  const { data, loading, refetch } = useGet("/staff/events");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const events = (data || []).filter((event) => {
    const query = q.toLowerCase();
    const matchesQuery = !query || [
      event.couple_names,
      event.event_type,
      event.venue,
      event.event_date,
    ].some((value) => value?.toLowerCase().includes(query));
    const matchesFilter = filter === "all"
      || (filter === "invite" && event.client_count === 0)
      || (filter === "planning" && event.next_action?.kind === "milestone")
      || (filter === "run_sheet" && event.run_sheet_approval === "pending");
    return matchesQuery && matchesFilter;
  });

  const create = async () => {
    if (!form.couple_names.trim()) { toast.error("Couple / event name is required."); return; }
    setBusy(true);
    try { await api.post("/staff/events", form); toast.success("Event created."); setOpen(false); setForm(BLANK); refetch(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <Loading label="Loading events" />;

  return (
    <div>
      <PageHeader eyebrow="Gledswood Operations" title="Events" description="Every event you manage. Create an event, then invite the couple.">
        <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={() => { setForm(BLANK); setOpen(true); }} data-testid="create-event-btn"><Plus size={18} className="mr-1" /> New event</Button>
      </PageHeader>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative max-w-md flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search couple, venue or date…" className="pl-10 rounded-full" data-testid="event-search-input" />
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="event-list-filters">
          <FunnelSimple size={18} className="text-muted-foreground" />
          {[
            ["all", "All"], ["invite", "Need invite"], ["planning", "In planning"], ["run_sheet", "Run sheet"],
          ].map(([value, label]) => (
            <Button
              key={value}
              type="button"
              variant={filter === value ? "default" : "outline"}
              size="sm"
              className="rounded-full"
              onClick={() => setFilter(value)}
              data-testid={`event-filter-${value}`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {events.length === 0 ? (
        <EmptyState icon={CalendarPlus} title="No events yet" description="Create your first event to begin inviting clients."
          action={<Button className="rounded-full bg-primary" onClick={() => setOpen(true)}>Create event</Button>} />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {events.map((e) => (
            <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`} className="group rounded-lg border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="rounded-full text-accent border-accent/30">{e.event_type}</Badge>
                <ArrowRight size={18} className="text-muted-foreground group-hover:text-primary transition-colors duration-200" />
              </div>
              <h3 className="font-heading text-2xl text-primary mt-3">{e.couple_names}</h3>
              <p className="text-sm text-muted-foreground mt-1" data-testid={`event-schedule-${e.id}`}>
                {formatEventSchedule(e.event_date, e.event_time)}
              </p>
              <div className="flex items-center gap-2 mt-4">
                <Badge variant="outline" className="rounded-full">{e.progress.complete}/{e.progress.total} milestones</Badge>
              </div>
              <p className="mt-4 text-sm text-primary" data-testid={`event-next-action-${e.id}`}>
                Next: {e.next_action?.label || "Review event"}
              </p>
            </Link>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-2xl font-light">New event</DialogTitle>
            <DialogDescription>Create the event first — you'll invite the couple afterwards.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div><Label className="eyebrow">Event type</Label><Input className="mt-2" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} data-testid="event-type-input" /></div>
            <div><Label className="eyebrow">Couple / event name</Label><Input className="mt-2" value={form.couple_names} onChange={(e) => setForm({ ...form, couple_names: e.target.value })} placeholder="Ava & Jordan" data-testid="event-name-input" /></div>
            <div><Label className="eyebrow">Date</Label><Input type="date" className="mt-2" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="event-date-input" /></div>
            <div><Label className="eyebrow">Time</Label><Input type="time" className="mt-2" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label className="eyebrow">Venue</Label><Input className="mt-2" value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-primary hover:bg-primary/90" onClick={create} disabled={busy} data-testid="save-event-btn">{busy ? "Creating…" : "Create event"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
